import {
  ActivityType,
  SalesTaxReturnsActivityType,
  SalesTaxTransactionsActivityType,
} from '@commenda/api/sales-tax/types';
import { Tooltip } from '@commenda/kakashi';
import { classNames } from '@commenda/utils';

interface StatItem<T> {
  type: T;
  title: string;
  value: number | string;
  /** Full value shown on hover (e.g., "$1,234,567.89") */
  fullValue?: string;
  subTitle?: string;
  subTitlePosition?: 'before' | 'after';
}

interface StatsOverviewProps<T> {
  items: StatItem<T>[];
  rowCount?: number;
  getTextColorClass: (type: string) => string;
}

const StatsOverview = ({
  items = [],
  rowCount = 1,
  getTextColorClass,
}: StatsOverviewProps<
  ActivityType | SalesTaxTransactionsActivityType | SalesTaxReturnsActivityType
>) => {
  const totalItems = items.length;
  const effectiveRowCount = Math.max(1, rowCount);
  const columnCount =
    totalItems > 0 ? Math.ceil(totalItems / effectiveRowCount) : 1;

  const gridColsClass = `grid-cols-${columnCount}`;
  const rowCountToRender =
    totalItems > 0 ? Math.ceil(totalItems / columnCount) : 1;

  return (
    <div
      className={classNames(
        'border border-border-primary rounded-2xl overflow-clip grid',
        gridColsClass
      )}
    >
      {Array.from({ length: rowCountToRender }).map((_, rowIndex) =>
        Array.from({ length: columnCount }).map((__, colIndex) => {
          const index = rowIndex * columnCount + colIndex;
          const item = items[index];

          return (
            <div
              // Use a stable key for real items, and a fallback for placeholders.
              key={item ? item.type : `placeholder-${index}`}
              className={classNames(
                'p-2xl flex flex-col justify-between gap-5 h-full',
                rowIndex > 0 && 'border-t border-border-primary',
                colIndex > 0 && 'border-l border-border-primary'
              )}
            >
              {item && (
                <>
                  <div className="flex items-center gap-1">
                    <p
                      className={`${getTextColorClass(item.type)} txt-extra-small-medium uppercase`}
                    >
                      {item.title}
                    </p>
                  </div>
                  <Tooltip
                    content={item.fullValue}
                    side="bottom"
                    disabled={!item.fullValue}
                  >
                    <div className="flex items-end gap-1 cursor-default">
                      {item.subTitle && item.subTitlePosition === 'before' && (
                        <p className="text-text-quaternary display-sm-regular">
                          {item.subTitle}
                        </p>
                      )}
                      <p className="display-sm-medium text-text-primary">
                        {item.value}
                      </p>
                      {item.subTitle &&
                        (!item.subTitlePosition ||
                          item.subTitlePosition === 'after') && (
                          <p className="txt-lg text-text-quaternary pb-0.5">
                            {item.subTitle}
                          </p>
                        )}
                    </div>
                  </Tooltip>
                </>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default StatsOverview;
